<template lang="pug">
div(
    class="flex"
    :class="alignmentClasses"
)
    UniversalLink(
        :link="data.linkUrl"
        :openNewWindow="data.linkTarget === 'NEW_WINDOW'"
    )
        Picture(v-bind="pictureBindings")
</template>

<script>
import Picture from '@/components/picture'
import UniversalLink from '@/components/universalLink'
import { getMaxWidth, generateFlexAlignmentClasses } from '../../helpers'

export default {
    name: 'SvgWidget',
    components: {
        UniversalLink,
        Picture,
    },
    inject: ['$story'],
    props: {
        data: {
            type: Object,
            default() {
                return {}
            },
        },
        spaceLeft: {
            type: Object,
            default() {
                return {}
            },
        },
        forceLazyLoad: {
            type: Boolean,
            default: false,
        },
        isImportant: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            shouldLazyLoad: false,
        }
    },
    computed: {
        linkUrl() {
            if (this.data.linkUrl) return this.data.linkUrl
            return false
        },
        pictureBindings() {
            // Get the src, Width, and Height
            const imageData = this.getSvgData()
            return {
                alt: this.data.alt || '', // Empty alt will be considered decorative
                isLazy: this.shouldLazyLoad,
                isImportant: this.isImportant,
                ...imageData,
            }
        },
        maxWidth() {
            return getMaxWidth(this.data)
        },
        alignmentClasses() {
            return generateFlexAlignmentClasses(
                this.data.mobileAlignment,
                this.data.tabletAlignment,
                this.data.desktopAlignment
            )
        },
    },
    created() {
        this.shouldLazyLoad = this.$story.shouldLazyLoad(this.forceLazyLoad)
    },
    methods: {
        getSvgData() {
            const sources = {
                mobile: {},
                tablet: {},
                desktop: {},
            }

            if (
                this.data.mobileImage.filename &&
                this.data.mobileSvgSize?.width &&
                this.data.mobileSvgSize?.height
            ) {
                const formattedMobileSrc = this.$storyblok.formatSrc(this.data.mobileImage.filename)
                sources.mobile.src = formattedMobileSrc
                sources.tablet.src = formattedMobileSrc
                sources.desktop.src = formattedMobileSrc
                sources.mobile.width = this.data.mobileSvgSize.width
                sources.mobile.height = this.data.mobileSvgSize.height
                sources.tablet.width = this.data.mobileSvgSize.width
                sources.tablet.height = this.data.mobileSvgSize.height
                sources.desktop.width = this.data.mobileSvgSize.width
                sources.desktop.height = this.data.mobileSvgSize.height
            }

            if (
                this.data.tabletImage.filename &&
                this.data.tabletSvgSize?.width &&
                this.data.tabletSvgSize?.height
            ) {
                const formattedTabletSrc = this.$storyblok.formatSrc(this.data.tabletImage.filename)
                sources.tablet.src = formattedTabletSrc
                sources.desktop.src = formattedTabletSrc
                sources.tablet.width = this.data.tabletSvgSize.width
                sources.tablet.height = this.data.tabletSvgSize.height
                sources.desktop.width = this.data.tabletSvgSize.width
                sources.desktop.height = this.data.tabletSvgSize.height
            }

            if (
                this.data.desktopImage.filename &&
                this.data.desktopSvgSize?.width &&
                this.data.desktopSvgSize?.height
            ) {
                sources.desktop.src = this.$storyblok.formatSrc(this.data.desktopImage.filename)
                sources.desktop.width = this.data.desktopSvgSize.width
                sources.desktop.height = this.data.desktopSvgSize.height
            }

            const {
                src: mobileSrc,
                width: mobileWidth,
                height: mobileHeight,
            } = this.$storyblok.getNormalizedAsset(
                sources.mobile,
                this.maxWidth.mobile || this.spaceLeft.mobile
            )

            const {
                src: tabletSrc,
                width: tabletWidth,
                height: tabletHeight,
            } = this.$storyblok.getNormalizedAsset(
                sources.tablet,
                this.maxWidth.tablet || this.spaceLeft.tablet
            )

            const {
                src: desktopSrc,
                width: desktopWidth,
                height: desktopHeight,
            } = this.$storyblok.getNormalizedAsset(
                sources.desktop,
                this.maxWidth.desktop || this.spaceLeft.desktop
            )

            return {
                ...(mobileSrc && {
                    mobileSrc,
                    mobileWidth,
                    mobileHeight,
                }),
                ...(tabletSrc && {
                    tabletSrc,
                    tabletWidth,
                    tabletHeight,
                }),
                ...(desktopSrc && {
                    desktopSrc,
                    desktopWidth,
                    desktopHeight,
                }),
            }
        },
    },
}
</script>
